import React, { FC, useEffect, useState } from 'react'

import { Storage } from '@material-ui/icons'
import TabTile from 'tabs/data/components/TabTile'
import { Table } from 'tabs/data/api/generated'
import { Highlighted } from 'tabs/data/components/Highlighted'
import { DatasetColumns } from '../DatasetColumns'
import { DatasetTableTabs } from './DatasetTableTabs'
import { DatasetMetrics } from '../DatasetMetrics'
import TableForm from './TableForm'
import { Option } from 'helpers/selects'
import { deleteDatasetTable } from 'tabs/data/api/tables'

type DatasetTableProps = {
  table: Table
  highlight: string
  datasetId: string
  tableOptions: Option[]
  refreshCallback: () => Promise<void>
  onChange: (
    tableId: string,
    params: {
      tableDescription: string
      tableName: string
      tableVersion: number
      joinedTableIds: string[]
      tableSynonyms: string[]
      isActive: boolean
    },
  ) => void
}

export const DatasetTable: FC<DatasetTableProps> = ({
  table,
  highlight,
  datasetId,
  tableOptions,
  refreshCallback,
  onChange,
}) => {
  const [expandedTable, setExpandedTable] = useState(false)
  const [selectedDatasetIndex, setSelectedDatasetIndex] = useState<number>(0)

  const {
    tableId,
    tableName,
    tableDescription,
    tableVersion,
    joinedTableIds,
    updatedAt,
    metrics = [],
    columns = [],
    tableSynonyms = [],
    isActive = true,
  } = table

  const metricsCount = metrics?.length || 0
  const columnsCount = columns?.length || 0

  const tabs = ['Table', `Columns (${columnsCount})`, `Metrics (${metricsCount})`]

  const joinedTableOptions = tableOptions.filter(({ value }) => value !== table?.tableId)

  const handleToggleExpand = () => {
    setExpandedTable(!expandedTable)
  }

  const handleTableChange = async (
    tableName: string,
    tableDescription: string,
    joinedTableIds: string[],
    tableSynonyms: string[],
    isActive: boolean,
  ): Promise<void> => {
    return onChange(tableId, {
      tableDescription,
      tableName,
      tableVersion,
      joinedTableIds,
      tableSynonyms,
      isActive,
    })
  }

  const handleTableDelete = async (forceDelete: boolean) => {
    return await deleteDatasetTable(datasetId, tableId, forceDelete).then(async () => {
      await refreshCallback()
    })
  }

  useEffect(() => {
    if (!expandedTable) setSelectedDatasetIndex(0)
  }, [expandedTable])

  return (
    <TabTile
      expanded={expandedTable}
      tab={{
        name: <Highlighted text={tableName} highlight={highlight} />,
        icon: <Storage fill={'var(--bs-gray-600)!important'} />,
        description: `${columnsCount} columns | ${metricsCount} metrics${!isActive ? ' (Inactive)' : ''}`,
      }}
      onChange={handleToggleExpand}>
      {expandedTable && (
        <div>
          <DatasetTableTabs tabs={tabs} value={selectedDatasetIndex} setValue={setSelectedDatasetIndex} />

          {selectedDatasetIndex === 0 && (
            <TableForm
              isFromEdit
              updatedAt={updatedAt}
              cancelButtonLabel="Reset"
              submitButtonLabel="Save"
              inputDescription="Table name"
              textDescription="Description"
              options={joinedTableOptions}
              inputText={tableName}
              textAreaContent={tableDescription}
              joinedTables={joinedTableIds}
              synonyms={tableSynonyms}
              isActive={isActive}
              onSubmit={handleTableChange}
              onDelete={handleTableDelete}
            />
          )}

          {selectedDatasetIndex === 1 && (
            <DatasetColumns
              datasetId={datasetId}
              tableId={tableId}
              columns={columns}
              highlight={highlight}
              refreshCallback={refreshCallback}
            />
          )}

          {selectedDatasetIndex === 2 && (
            <DatasetMetrics
              datasetId={datasetId}
              tableId={tableId}
              metrics={metrics}
              highlight={highlight}
              refreshCallback={refreshCallback}
            />
          )}
        </div>
      )}
    </TabTile>
  )
}
